import React, { useState, useEffect } from 'react';
import apiClient from '../services/api';

/**
 * UserManagement.jsx
 * Full CRUD user management: list, create, edit, delete, role assignment
 * Props: lang, theme
 */

const UserManagement = ({ lang = 'lo', theme = 'dark' }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [form, setForm] = useState({ name: '', email: '', role: 'viewer', status: 'active', modules: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const i18n = {
    lo: {
      title: 'ຜູ້ໃຊ້ລະບົບ',
      subtitle: 'ຈັດການສິດທິ ແລະ ບັນຊີຜູ້ໃຊ້',
      addUser: 'ເພີ່ມຜູ້ໃຊ້',
      name: 'ຊື່',
      email: 'ອີເມວ',
      role: 'ບົດບາດ',
      status: 'ສະຖານະ',
      modules: 'ໂມດູນ',
      actions: 'ການກະທຳ',
      edit: 'ແກ້ໄຂ',
      delete: 'ລຶບ',
      save: 'ບັນທຶກ',
      cancel: 'ຍົກເລີກ',
      confirmDelete: 'ຢືນຢັນການລຶບ',
      deleteMsg: 'ທ່ານຕ້ອງການລຶບຜູ້ໃຊ້',
      search: 'ຄົ້ນຫາ...',
      allRoles: 'ທຸກບົດບາດ',
      admin: 'ຜູ້ດູແລ',
      manager: 'ຜູ້ຈັດການ',
      viewer: 'ຜູ້ເບິ່ງ',
      operator: 'ຜູ້ດຳເນີນການ',
      active: 'ໃຊ້ງານ',
      inactive: 'ລະງັບ',
      lastLogin: 'ເຂົ້າສຸດທ້າຍ',
      created: 'ສ້າງວັນທີ',
      total: 'ທັງໝົດ',
      editUser: 'ແກ້ໄຂຜູ້ໃຊ້',
      newUser: 'ຜູ້ໃຊ້ໃໝ່',
      selectModules: 'ເລືອກໂມດູນ',
    },
    th: {
      title: 'ผู้ใช้ระบบ',
      subtitle: 'จัดการสิทธิ์และบัญชีผู้ใช้',
      addUser: 'เพิ่มผู้ใช้',
      name: 'ชื่อ',
      email: 'อีเมล',
      role: 'บทบาท',
      status: 'สถานะ',
      modules: 'โมดูล',
      actions: 'การกระทำ',
      edit: 'แก้ไข',
      delete: 'ลบ',
      save: 'บันทึก',
      cancel: 'ยกเลิก',
      confirmDelete: 'ยืนยันการลบ',
      deleteMsg: 'คุณต้องการลบผู้ใช้',
      search: 'ค้นหา...',
      allRoles: 'ทุกบทบาท',
      admin: 'ผู้ดูแล',
      manager: 'ผู้จัดการ',
      viewer: 'ผู้ดู',
      operator: 'ผู้ดำเนินการ',
      active: 'ใช้งาน',
      inactive: 'ระงับ',
      lastLogin: 'เข้าสุดท้าย',
      created: 'สร้างวันที่',
      total: 'ทั้งหมด',
      editUser: 'แก้ไขผู้ใช้',
      newUser: 'ผู้ใช้ใหม่',
      selectModules: 'เลือกโมดูล',
    },
    en: {
      title: 'User Management',
      subtitle: 'Manage user accounts and permissions',
      addUser: 'Add User',
      name: 'Name',
      email: 'Email',
      role: 'Role',
      status: 'Status',
      modules: 'Modules',
      actions: 'Actions',
      edit: 'Edit',
      delete: 'Delete',
      save: 'Save',
      cancel: 'Cancel',
      confirmDelete: 'Confirm Delete',
      deleteMsg: 'Are you sure you want to delete',
      search: 'Search...',
      allRoles: 'All Roles',
      admin: 'Admin',
      manager: 'Manager',
      viewer: 'Viewer',
      operator: 'Operator',
      active: 'Active',
      inactive: 'Inactive',
      lastLogin: 'Last Login',
      created: 'Created',
      total: 'Total',
      editUser: 'Edit User',
      newUser: 'New User',
      selectModules: 'Select Modules',
    },
  };

  const t = (key) => i18n[lang]?.[key] || i18n.en[key] || key;

  // Mock users
  const mockUsers = [
    { id: 1, name: 'Souvanxay Phonemixay', email: 'souvanxay@roadfund.la', role: 'admin', status: 'active', modules: ['toll', 'bridge', 'wim', 'auction', 'topup'], lastLogin: '2026-03-19 09:12', created: '2024-01-15', avatar: 'SP' },
    { id: 2, name: 'Khamla Vilayvong', email: 'khamla@roadfund.la', role: 'manager', status: 'active', modules: ['toll', 'bridge'], lastLogin: '2026-03-18 14:30', created: '2024-02-20', avatar: 'KV' },
    { id: 3, name: 'Bounsong Thammavong', email: 'bounsong@roadfund.la', role: 'operator', status: 'active', modules: ['toll'], lastLogin: '2026-03-19 08:00', created: '2024-03-10', avatar: 'BT' },
    { id: 4, name: 'Malee Chanthaphone', email: 'malee@roadfund.la', role: 'viewer', status: 'active', modules: ['toll', 'bridge', 'wim', 'auction', 'topup'], lastLogin: '2026-03-17 16:45', created: '2024-04-01', avatar: 'MC' },
    { id: 5, name: 'Phomma Ketkeo', email: 'phomma@roadfund.la', role: 'operator', status: 'inactive', modules: ['wim'], lastLogin: '2026-02-28 11:22', created: '2024-05-12', avatar: 'PK' },
    { id: 6, name: 'Naly Sisomphone', email: 'naly@roadfund.la', role: 'manager', status: 'active', modules: ['auction', 'bridge'], lastLogin: '2026-03-18 10:05', created: '2024-06-03', avatar: 'NS' },
  ];

  useEffect(() => {
    // Try API, fallback to mock
    const load = async () => {
      try {
        const res = await apiClient.usersGetAll?.();
        if (res?.success && res?.data?.length) {
          setUsers(res.data);
        } else {
          setUsers(mockUsers);
        }
      } catch {
        setUsers(mockUsers);
      }
      setLoading(false);
    };
    load();
  }, []);

  const roleColors = {
    admin: { bg: 'rgba(245,34,45,.15)', color: '#f5222d' },
    manager: { bg: 'rgba(24,144,255,.15)', color: '#1890ff' },
    operator: { bg: 'rgba(250,140,22,.15)', color: '#fa8c16' },
    viewer: { bg: 'rgba(82,196,26,.15)', color: '#52c41a' },
  };

  const moduleLabels = {
    toll: { lo: 'ທາງດ່ວນ', th: 'ทางด่วน', en: 'Toll' },
    bridge: { lo: 'ສະພານ', th: 'สะพาน', en: 'Bridge' },
    wim: { lo: 'WIM', th: 'WIM', en: 'WIM' },
    auction: { lo: 'ປະມູນ', th: 'ประมูล', en: 'Auction' },
  };

  const filteredUsers = users.filter(u => {
    const matchSearch =
      (u.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (u.email || '').toLowerCase().includes(search.toLowerCase());
    const matchRole = filterRole === 'all' || (u.role || '') === filterRole;
    return matchSearch && matchRole;
  });

  const openCreate = () => {
    setEditingUser(null);
    setForm({ name: '', email: '', role: 'viewer', status: 'active', modules: [] });
    setModalOpen(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setForm({ name: user.name || '', email: user.email || '', role: user.role || 'viewer', status: user.status || 'active', modules: [...(user.modules || [])] });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.email) return;
    setSaving(true);
    setError(null);
    try {
      if (editingUser) {
        // Update via API
        const res = await apiClient.usersUpdate(editingUser.id, form);
        if (res && res.success) {
          setUsers(prev => prev.map(u => u.id === editingUser.id ? res.data : u));
          setModalOpen(false);
        } else {
          throw new Error(res?.error || 'Failed to update user');
        }
      } else {
        // Create via API
        const res = await apiClient.usersCreate(form);
        if (res && res.success) {
          setUsers(prev => [res.data, ...prev]);
          setModalOpen(false);
        } else {
          throw new Error(res?.error || 'Failed to create user');
        }
      }
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  const handleDelete = async (user) => {
    setError(null);
    try {
      const res = await apiClient.usersDelete(user.id);
      if (res && res.success) {
        setUsers(prev => prev.filter(u => u.id !== user.id));
        setDeleteConfirm(null);
      } else {
        throw new Error(res?.error || 'Failed to delete user');
      }
    } catch (e) {
      setError(e.message);
    }
  };

  const toggleModule = (mod) => {
    setForm(f => ({
      ...f,
      modules: f.modules.includes(mod) ? f.modules.filter(m => m !== mod) : [...f.modules, mod],
    }));
  };

  // Stats
  const stats = [
    { label: t('total'), value: users.length, color: '#1890ff' },
    { label: t('admin'), value: users.filter(u => u.role === 'admin').length, color: '#f5222d' },
    { label: t('active'), value: users.filter(u => u.status === 'active').length, color: '#52c41a' },
    { label: t('inactive'), value: users.filter(u => u.status === 'inactive').length, color: '#fa8c16' },
  ];

  const inputStyle = {
    padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border)',
    background: 'var(--bg2)', color: 'var(--text)', width: '100%',
    fontSize: '13px', fontFamily: 'var(--font)', outline: 'none',
  };

  return (
    <div className="main">
      <div className="section-header">
        <div>
          <div className="section-title">{t('title')}</div>
          <div className="section-sub">{t('subtitle')}</div>
        </div>
        <button
          onClick={openCreate}
          style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', background: '#1890ff', color: '#fff', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: '13px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          + {t('addUser')}
        </button>
      </div>

      {/* Stats */}
      <div className="kpi-grid" style={{ marginBottom: '16px' }}>
        {stats.map((s) => (
          <div key={s.label} className="kpi">
            <div className="kpi-accent" style={{ background: s.color }} />
            <div className="kpi-label">{s.label}</div>
            <div className="kpi-value" style={{ color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
        <input
          type="text"
          placeholder={t('search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...inputStyle, width: '240px' }}
        />
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          style={{ ...inputStyle, width: '160px', cursor: 'pointer' }}
        >
          <option value="all">{t('allRoles')}</option>
          <option value="admin">{t('admin')}</option>
          <option value="manager">{t('manager')}</option>
          <option value="operator">{t('operator')}</option>
          <option value="viewer">{t('viewer')}</option>
        </select>
        <div style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--text3)' }}>
          {filteredUsers.length} / {users.length} {t('total')}
        </div>
      </div>

      {/* Users Table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text3)' }}>Loading...</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('name')}</th>
                <th>{t('email')}</th>
                <th>{t('role')}</th>
                <th>{t('modules')}</th>
                <th>{t('status')}</th>
                <th>{t('lastLogin')}</th>
                <th style={{ textAlign: 'center' }}>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
                        background: roleColors[u.role]?.bg,
                        color: roleColors[u.role]?.color,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '11px', fontWeight: '700',
                      }}>{u.avatar}</div>
                      <span style={{ fontWeight: '500', fontSize: '13px' }}>{u.name}</span>
                    </div>
                  </td>
                  <td style={{ color: 'var(--text3)', fontSize: '12px', fontFamily: 'var(--mono)' }}>{u.email}</td>
                  <td>
                    <span style={{ padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', ...roleColors[u.role] }}>
                      {t(u.role)}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {(u.modules || []).map((m) => (
                        <span key={m} style={{ padding: '2px 6px', borderRadius: '4px', fontSize: '10px', background: 'var(--bg4)', color: 'var(--text2)' }}>
                          {moduleLabels[m]?.[lang] || m}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <span style={{
                      padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
                      background: u.status === 'active' ? 'rgba(82,196,26,.15)' : 'rgba(250,140,22,.15)',
                      color: u.status === 'active' ? '#52c41a' : '#fa8c16',
                    }}>
                      {u.status === 'active' ? `✓ ${t('active')}` : `⏸ ${t('inactive')}`}
                    </span>
                  </td>
                  <td style={{ fontSize: '11px', color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{u.lastLogin}</td>
                  <td style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                      <button
                        onClick={() => openEdit(u)}
                        style={{ padding: '4px 12px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text2)', cursor: 'pointer', fontSize: '11px', fontFamily: 'var(--font)' }}
                      >
                        ✏ {t('edit')}
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(u)}
                        style={{ padding: '4px 12px', borderRadius: '4px', border: '1px solid rgba(245,34,45,.3)', background: 'rgba(245,34,45,.08)', color: '#f5222d', cursor: 'pointer', fontSize: '11px', fontFamily: 'var(--font)' }}
                      >
                        ✕ {t('delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create / Edit Modal */}
      {modalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '12px', padding: '28px', width: '480px', maxWidth: '95vw' }}>
            <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text)', marginBottom: '20px' }}>
              {editingUser ? t('editUser') : t('newUser')}
            </div>

            {error && (
              <div style={{ color: '#f5222d', background: 'rgba(245,34,45,.1)', padding: '10px 12px', borderRadius: '6px', marginBottom: '16px', fontSize: '12px', fontFamily: 'var(--font)' }}>
                ⚠️ {error}
              </div>
            )}

            <div style={{ display: 'grid', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--text3)', display: 'block', marginBottom: '6px' }}>{t('name')}</label>
                <input type="text" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--text3)', display: 'block', marginBottom: '6px' }}>{t('email')}</label>
                <input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} style={inputStyle} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text3)', display: 'block', marginBottom: '6px' }}>{t('role')}</label>
                  <select value={form.role} onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="admin">{t('admin')}</option>
                    <option value="manager">{t('manager')}</option>
                    <option value="operator">{t('operator')}</option>
                    <option value="viewer">{t('viewer')}</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text3)', display: 'block', marginBottom: '6px' }}>{t('status')}</label>
                  <select value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="active">{t('active')}</option>
                    <option value="inactive">{t('inactive')}</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--text3)', display: 'block', marginBottom: '8px' }}>{t('selectModules')}</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {['toll', 'bridge', 'wim', 'auction', 'topup'].map((m) => (
                    <button
                      key={m}
                      onClick={() => toggleModule(m)}
                      style={{
                        padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontFamily: 'var(--font)',
                        border: form.modules.includes(m) ? `1px solid #1890ff` : '1px solid var(--border)',
                        background: form.modules.includes(m) ? 'rgba(24,144,255,.15)' : 'var(--bg3)',
                        color: form.modules.includes(m) ? '#1890ff' : 'var(--text2)',
                        fontWeight: form.modules.includes(m) ? '600' : '400',
                      }}
                    >
                      {form.modules.includes(m) ? '✓ ' : ''}{moduleLabels[m]?.[lang] || m}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button onClick={() => setModalOpen(false)} style={{ padding: '8px 20px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text2)', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: '13px' }}>
                {t('cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name || !form.email}
                style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', background: '#1890ff', color: '#fff', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: '13px', fontWeight: '500', opacity: saving ? 0.7 : 1 }}
              >
                {saving ? '...' : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '12px', padding: '28px', width: '360px' }}>
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#f5222d', marginBottom: '12px' }}>⚠ {t('confirmDelete')}</div>
            <div style={{ fontSize: '13px', color: 'var(--text2)', marginBottom: '20px' }}>
              {t('deleteMsg')} <strong style={{ color: 'var(--text)' }}>{deleteConfirm.name}</strong>?
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ padding: '8px 20px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text2)', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: '13px' }}>
                {t('cancel')}
              </button>
              <button onClick={() => handleDelete(deleteConfirm)} style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', background: '#f5222d', color: '#fff', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: '13px', fontWeight: '500' }}>
                {t('delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
